// Client for /api/myDonation/* (MyDonationController). Every data call sends the session's
// legal entity + operation unit; the backend re-validates both against the user's access.

import { apiDownload, apiFetch } from "@/lib/api";

export interface IdName {
  id: number;
  name: string;
}

export interface BudgetYear extends IdName {
  startDate: string; // yyyy-mm-dd
  endDate: string;
}

export interface ContextOptions {
  legalEntities: IdName[];
  /** legalEntityIds = LEs this OU is mapped to (empty when the mapping is not maintained). */
  operationUnits: (IdName & { legalEntityIds: number[] })[];
  budgetYears: BudgetYear[];
  currentBudgetYearId: number | null;
}

export interface SessionScope {
  legalEntity: IdName;
  operationUnit: IdName;
  /** Optional (mandatory when departmentLocked); null = all departments of the operation unit. */
  department?: IdName | null;
  /** True → the user is a member of fundraising department(s) and must work within them. */
  departmentLocked?: boolean;
}

export interface DepartmentOptions {
  departments: IdName[];
  locked: boolean;
}

export interface Cause extends IdName {
  isActive: boolean;
}

export interface Totals {
  amount: number;
  count: number;
  donors: number;
  average: number;
}

export interface DonorProfile {
  donor: {
    id: number;
    name: string | null;
    mobile: string | null;
    email: string | null;
    contactNotes: string | null;
    isOrganization: boolean;
    pan: string | null;
    displayName: string | null;
    legalName: string | null;
    initiatedName: string | null;
    spiritualMaster: string | null;
    receiptName: string | null;
    dob: string | null;
    dobAuthenticity: string | null;
    gender: string | null;
    maritalStatus: string | null;
    type: string | null;
    center: string | null;
    panVerified: boolean | null;
    gstn: string | null;
    whatsAppNumber: string | null;
    referredBy: string | null;
    satsangas: string | null;
    isDeceased: boolean | null;
    privacy: {
      donNotCall: boolean | null;
      doNotEmail: boolean | null;
      doNotSms: boolean | null;
      noBirthdayWish: boolean | null;
      noAnniversaryWish: boolean | null;
    };
  };
  contacts: {
    mobiles: { number: string; location: string | null; isPrimary: boolean }[];
    emails: { email: string; location: string | null; isPrimary: boolean; verified: boolean }[];
    phones: { number: string; location: string | null; isPrimary: boolean }[];
  };
  addresses: {
    id: number;
    location: string | null;
    lines: string;
    city: string | null;
    state: string | null;
    zip: string | null;
    country: string | null;
    isPrimary: boolean;
    phone: string | null;
    email: string | null;
  }[];
  anniversaries: { date: string | null; type: string | null; authenticity: string | null; remarks: string | null }[];
  relationships: {
    id: number | null;
    name: string | null;
    relationship: string | null;
    mobile: string | null;
    email: string | null;
    dob: string | null;
    gender: string | null;
  }[];
  /** Organisations this donor is a contact of. */
  organizations: {
    id: number;
    donorId: number | null;
    name: string | null;
    designation: string | null;
    remarks: string | null;
    mobile: string | null;
    email: string | null;
  }[];
  /** People who are contacts of this donor (when the donor is an organisation). */
  orgContacts: {
    id: number;
    donorId: number | null;
    name: string | null;
    designation: string | null;
    remarks: string | null;
    mobile: string | null;
    email: string | null;
  }[];
  recurringPlans: {
    id: number;
    cause: string | null;
    location: string | null;
    amount: number;
    startDate: string | null;
    endDate: string | null;
    repeats: string;
    active: boolean;
  }[];
  yearSummary: { id: number; name: string; amount: number; count: number }[];
  gifts: {
    id: number;
    gift: string | null;
    deliveryDate: string | null;
    deliveryMethod: string | null;
    value: number;
    description: string | null;
    budgetYear: string | null;
    receiptNumber: string | null;
  }[];
  donorNotes: { id: number; note: string | null; date: string | null; by: string | null }[];
  tags: string[];
  budgetYear: IdName;
  totals: {
    lifetime: CountAmount;
    fy: CountAmount;
    pending: CountAmount;
    firstDonation: string | null;
    lastDonation: string | null;
  };
  donations: {
    id: number;
    receiptNumber: string | null;
    date: string | null;
    amount: number;
    modeOfPayment: string | null;
    causes: string | null;
    received: boolean;
    /** Others credited for this donation ("in honour of", "in memory of", …). */
    softCredits: { name: string | null; type: string | null; amount: number; remarks: string | null }[];
  }[];
}

export interface Breakdown {
  id: number | null;
  name: string;
  amount: number;
  count: number;
}

export interface MonthRow {
  year: number;
  month: number; // 1-12
  amount: number;
  count: number;
  prevAmount: number;
}

export interface RecentDonation {
  donorId: number | null;
  donorName: string | null;
  mobile?: string | null;
  email?: string | null;
  id: number;
  receiptNumber: string | null;
  receiptDate: string | null;
  nameOnReceipt: string | null;
  amount: number;
  modeOfPayment: string | null;
  received: boolean;
}

export interface Dashboard {
  context: {
    legalEntity: IdName;
    operationUnit: IdName;
    department: IdName | null;
    cause: IdName | null;
    budgetYear: BudgetYear;
    prevBudgetYear: IdName | null;
    isCurrentYear: boolean;
    asOf: string;
  };
  kpis: {
    fy: Totals;
    prevFyToDate: Totals | null;
    month: Totals;
    today: Totals | null;
    pending: Totals;
    eightyG: number;
  };
  monthly: MonthRow[];
  byModeOfPayment: Breakdown[];
  byOccasion: Breakdown[];
  byDepartment: Breakdown[];
  /** mobile / email only for users who may see donor details. */
  topDonors: (Breakdown & { mobile?: string | null; email?: string | null })[];
  recent: RecentDonation[];
  budgetYears: BudgetYear[];
}

export function fetchContextOptions(token: string, signal?: AbortSignal) {
  return apiFetch<ContextOptions>("/api/myDonation/apiContextOptions", {
    token,
    method: "POST",
    body: {},
    signal,
  });
}

export function fetchDepartments(token: string, operationUnitId: number, signal?: AbortSignal) {
  return apiFetch<DepartmentOptions>("/api/myDonation/apiDepartments", {
    token,
    method: "POST",
    body: { operationUnitId },
    signal,
  });
}

/** Causes of the OU, narrowed to the department when one is given (dashboard / reports filter). */
export function fetchCauses(
  token: string,
  operationUnitId: number,
  departmentId: number | null,
  signal?: AbortSignal
) {
  return apiFetch<Cause[]>("/api/myDonation/apiCauses", {
    token,
    method: "POST",
    body: { operationUnitId, departmentId },
    signal,
  });
}

export function validateContext(
  token: string,
  legalEntityId: number,
  operationUnitId: number,
  departmentId: number | null
) {
  return apiFetch<SessionScope>("/api/myDonation/apiValidateContext", {
    token,
    method: "POST",
    body: { legalEntityId, operationUnitId, departmentId },
  });
}

/** Per-page filters on top of the session scope. */
export interface PageFilters {
  budgetYearId: number | null;
  causeId: number | null;
}

function scopeBody(scope: SessionScope, causeId: number | null) {
  return {
    legalEntityId: scope.legalEntity.id,
    operationUnitId: scope.operationUnit.id,
    departmentId: scope.department?.id ?? null,
    causeId,
  };
}

export function fetchDashboard(token: string, scope: SessionScope, f: PageFilters, signal?: AbortSignal) {
  return apiFetch<Dashboard>("/api/myDonation/apiDashboard", {
    token,
    method: "POST",
    body: { ...scopeBody(scope, f.causeId), budgetYearId: f.budgetYearId },
    signal,
  });
}

const INR = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });
const INR_COMPACT = new Intl.NumberFormat("en-IN", { notation: "compact", maximumFractionDigits: 1 });

export function formatAmount(value?: number | null): string {
  if (value == null || Number.isNaN(value)) return "—";
  return `₹${INR.format(value)}`;
}

/** Axis/label form: ₹1.2L, ₹3.4Cr (Indian compact notation). */
export function formatAmountCompact(value?: number | null): string {
  if (value == null || Number.isNaN(value)) return "—";
  return `₹${INR_COMPACT.format(value)}`;
}

// ------------------------------------------------------------------ insights

export interface CountAmount {
  count: number;
  amount: number;
}

export interface Band extends CountAmount {
  label: string;
}

export interface Insights {
  donors: {
    newCount: number;
    newAmount: number;
    /** New donors this FY who have already donated again. */
    repeatDonorCount: number;
    lapsedCount: number;
    lapsedPrevAmount: number;
    anniversaryDays: number;
    anniversaryCount: number;
    donationAnniversaryCount: number;
  };
  donationSizeBands: Band[];
  pipeline: {
    pendingAging: Band[];
    pledges: { openCount: number; pledged: number; paid: number; overdue: CountAmount; due30: CountAmount };
    recurring: { activeCount: number; activeAmount: number; dueThisMonth: CountAmount; missed: CountAmount };
  };
  operations: {
    chequesUndeposited: CountAmount;
    cashToday: CountAmount | null;
    eightyGNoPan: CountAmount;
    tallyPending: CountAmount;
  };
  /** False → the named lists (Reports) are not available to this user. */
  canViewDetails: boolean;
}

export type InsightListType =
  | "newDonors"
  | "lapsedDonors"
  | "anniversaries"
  | "donationAnniversaries"
  | "pendingReceipts"
  | "pledgeInstallments"
  | "recurringMissed"
  | "chequesUndeposited"
  | "eightyGNoPan"
  | "tallyPending";

export interface InsightColumn {
  key: string;
  label: string;
  type: "text" | "amount" | "date" | "number";
  /** "donor" → render the cell as the donor menu (Open / Call / WhatsApp / Email). */
  role?: "donor";
}

export interface InsightList {
  columns: InsightColumn[];
  rows: Record<string, string | number | null>[];
  total: number;
  truncated: boolean;
}

export type InsightSection = "donors" | "sizes" | "pipeline" | "operations";
export const INSIGHT_SECTIONS: InsightSection[] = ["donors", "sizes", "pipeline", "operations"];

/** One section of the insights — the dashboard requests the four in parallel (shorter requests). */
export function fetchInsights(
  token: string,
  scope: SessionScope,
  f: PageFilters,
  section: InsightSection,
  signal?: AbortSignal
) {
  return apiFetch<Partial<Insights>>("/api/myDonation/apiInsights", {
    token,
    method: "POST",
    body: { ...scopeBody(scope, f.causeId), budgetYearId: f.budgetYearId, section },
    signal,
  });
}

export function fetchInsightList(
  token: string,
  scope: SessionScope,
  type: InsightListType,
  opts: PageFilters & { days?: number },
  signal?: AbortSignal
) {
  return apiFetch<InsightList>("/api/myDonation/apiInsightList", {
    token,
    method: "POST",
    body: { ...scopeBody(scope, opts.causeId), type, budgetYearId: opts.budgetYearId, days: opts.days ?? null },
    signal,
  });
}

/** The same list as an .xlsx built on the server (all rows, narrowed by the screen's search text). */
export function downloadInsightListXlsx(
  token: string,
  scope: SessionScope,
  type: InsightListType,
  opts: PageFilters & { days?: number; search?: string }
) {
  return apiDownload(
    "/api/myDonation/apiInsightListXlsx",
    token,
    {
      ...scopeBody(scope, opts.causeId),
      type,
      budgetYearId: opts.budgetYearId,
      days: opts.days ?? null,
      search: opts.search ?? "",
    },
    `${type}.xlsx`
  );
}

/** One donor's profile and donations, within the session scope. */
export function fetchDonor(
  token: string,
  scope: SessionScope,
  donorId: number,
  f: PageFilters,
  signal?: AbortSignal
) {
  return apiFetch<DonorProfile>("/api/myDonation/apiDonor", {
    token,
    method: "POST",
    body: { ...scopeBody(scope, f.causeId), donorId, budgetYearId: f.budgetYearId },
    signal,
  });
}

// ------------------------------------------------------------------ donors list

export interface DonorRow {
  donorId: number;
  name: string | null;
  mobile: string | null;
  email: string | null;
  notes: string | null;
  fyAmount: number;
  fyCount: number;
  lifetimeAmount: number;
  lifetimeCount: number;
  firstDonation: string | null;
  lastDonation: string | null;
}

export type DonorSort = "fyAmount" | "lifetimeAmount" | "lastDonation" | "name";

export interface DonorsPage {
  rows: DonorRow[];
  total: number;
  page: number;
  pageSize: number;
  budgetYear: IdName;
}

/** Donors who gave in the session's scope — searched, sorted and paged on the server. */
export function fetchDonors(
  token: string,
  scope: SessionScope,
  f: PageFilters & { search?: string; sort?: DonorSort; page?: number },
  signal?: AbortSignal
) {
  return apiFetch<DonorsPage>("/api/myDonation/apiDonors", {
    token,
    method: "POST",
    body: {
      ...scopeBody(scope, f.causeId),
      budgetYearId: f.budgetYearId,
      search: f.search ?? "",
      sort: f.sort ?? "fyAmount",
      page: f.page ?? 1,
    },
    signal,
  });
}

// ------------------------------------------------------------------ donations list

export interface DonationRow {
  donationId: number;
  donorId: number | null;
  receiptNumber: string | null;
  date: string | null;
  name: string | null;
  amount: number;
  modeOfPayment: string | null;
  causes: string | null;
  received: boolean;
  /** The receipt claims an 80G deduction. */
  eightyG: boolean;
  /** 80G claimed but no PAN on the receipt — the donor cannot claim the deduction. */
  panMissing: boolean;
  mobile: string | null;
  email: string | null;
}

export type DonationSort = "dateDesc" | "dateAsc" | "amountDesc" | "amountAsc";
export type DonationStatus = "all" | "received" | "pending" | "80gNoPan";

export interface DonationsPage {
  rows: DonationRow[];
  total: number;
  totalAmount: number;
  page: number;
  pageSize: number;
  budgetYear: IdName;
  /** The window actually used (the FY, or the custom range when one was given). */
  dateFrom: string | null;
  dateTo: string | null;
  customRange: boolean;
  canViewDetails: boolean;
}

/** Optional date range (yyyy-mm-dd); either end may be left out. Replaces the FY when set. */
export interface DateRange {
  dateFrom?: string;
  dateTo?: string;
}

/** Donations in the session's scope — searched, filtered, sorted and paged on the server. */
export function fetchDonations(
  token: string,
  scope: SessionScope,
  f: PageFilters & DateRange & { search?: string; status?: DonationStatus; sort?: DonationSort; page?: number },
  signal?: AbortSignal
) {
  return apiFetch<DonationsPage>("/api/myDonation/apiDonations", {
    token,
    method: "POST",
    body: {
      ...scopeBody(scope, f.causeId),
      budgetYearId: f.budgetYearId,
      search: f.search ?? "",
      status: f.status && f.status !== "all" ? f.status : null,
      sort: f.sort ?? "dateDesc",
      page: f.page ?? 1,
      dateFrom: f.dateFrom || null,
      dateTo: f.dateTo || null,
    },
    signal,
  });
}

/** The donations list as a server-built .xlsx, with the screen's filters. */
export function downloadDonationsXlsx(
  token: string,
  scope: SessionScope,
  f: PageFilters & DateRange & { search?: string; status?: DonationStatus; sort?: DonationSort }
) {
  return apiDownload(
    "/api/myDonation/apiDonationsXlsx",
    token,
    {
      ...scopeBody(scope, f.causeId),
      budgetYearId: f.budgetYearId,
      search: f.search ?? "",
      status: f.status && f.status !== "all" ? f.status : null,
      sort: f.sort ?? "dateDesc",
      dateFrom: f.dateFrom || null,
      dateTo: f.dateTo || null,
    },
    "donations.xlsx"
  );
}

/** The donors list as a server-built .xlsx, with the screen's search / sort. */
export function downloadDonorsXlsx(
  token: string,
  scope: SessionScope,
  f: PageFilters & { search?: string; sort?: DonorSort }
) {
  return apiDownload(
    "/api/myDonation/apiDonorsXlsx",
    token,
    {
      ...scopeBody(scope, f.causeId),
      budgetYearId: f.budgetYearId,
      search: f.search ?? "",
      sort: f.sort ?? "fyAmount",
    },
    "donors.xlsx"
  );
}
